import { ContentTab } from '@src/types.ts/general';
import {
  CLIP_REVIEW_STATUSES,
  DEFAULT_CLIP_REVIEW_STATUS,
} from '@src/types.ts/video';
import {
  getLastActionEditor,
  getLastActionTimestamp,
} from '@src/utility/lastAction';
import {
  getAllPublishInfo,
  getMostRecentPublishInfo,
} from '@src/utility/publishInfo';
import { getAiDescriptionResponse } from '@src/utility/story';
import { getFormattedDurationForStoryVideo } from '@src/utility/timeFormat';

import type {
  DashboardStory,
  DatoSpeaker,
  OtherVideo,
  ShareableImageType,
} from '@src/types.ts/story';
import type { ClipReviewStatus } from '@src/types.ts/video';

export type ContentType = 'clip' | 'quote' | 'article' | 'social';

export const ALL_CONTENT_TYPES: ContentType[] = [
  'clip',
  'quote',
  'article',
  'social',
];

export interface ContentItem {
  id: string;
  storyId: string;
  storyTitle: string;
  title: string;
  /** Secondary line shown beneath the title (per-item text, else the story's AI description). */
  description: string;
  thumbnail?: string;
  /** Formatted MM:SS video duration; clips with a known duration only. */
  duration?: string;
  contentType: ContentType;
  updatedAt: Date;
  updatedBy: string;
  /** Speakers of the parent story (speakers are modeled per story in Dato). */
  speakers: DatoSpeaker[];
  /** True for clips that have been published (in any aspect ratio). */
  isPublished: boolean;
  /** Content Studio review workflow status; clips only (empty/unset = New). */
  reviewStatus?: ClipReviewStatus;
  tab: ContentTab;
  /** Original storage key for deep-linking (e.g., timestamp for blog/social) */
  contentKey?: string;
}

export const ITEMS_PER_PAGE = 20;

export const contentTypeLabels: Record<ContentType, string> = {
  clip: 'Clip',
  quote: 'Quote',
  article: 'Article',
  social: 'Social Post',
};

export const contentTypeColors: Record<
  ContentType,
  { bg: string; text: string }
> = {
  clip: { bg: 'var(--content-clips-bg)', text: 'var(--content-clips-text)' },
  quote: {
    bg: 'var(--content-quotes-bg)',
    text: 'var(--content-quotes-text)',
  },
  article: {
    bg: 'var(--content-articles-bg)',
    text: 'var(--content-articles-text)',
  },
  social: {
    bg: 'var(--content-social-bg)',
    text: 'var(--content-social-text)',
  },
};

const DESCRIPTION_MAX_LENGTH = 300;

/** Flatten rich text (HTML tags / markdown markers) into a short plain-text snippet. */
function toDescriptionSnippet(text: string | undefined | null): string {
  if (!text) return '';
  return text
    .replace(/<[^>]+>/g, ' ')
    .replace(/[#*`_~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, DESCRIPTION_MAX_LENGTH);
}

/**
 * A clip's most recent activity: the later of its last HUMAN action
 * (`lastActionJson`) and its last publish (`lastPublishJson`, including the
 * per-aspect-ratio associated videos). Publishing counts, so a clip that was
 * published but not hand-edited since still surfaces in Recent Content — the
 * same recency notion Content Studio's clip grid sorts by.
 *
 * Date and actor come from the same winner so the displayed date always
 * matches the displayed "Updated By" name. Dato's `_updatedAt` stays a
 * last-resort fallback only: it also moves on system writes (render status,
 * publish info) that no person is responsible for.
 */
function getClipActivity(
  video: OtherVideo,
): { timestamp: number; actor: string } | null {
  const rawActionAt = getLastActionTimestamp(video);
  const actionAt = Number.isNaN(rawActionAt) ? 0 : rawActionAt;

  const publishInfo = getMostRecentPublishInfo(getAllPublishInfo(video));
  const rawPublishAt = publishInfo
    ? new Date(publishInfo.timestamp).getTime()
    : 0;
  const publishAt = Number.isNaN(rawPublishAt) ? 0 : rawPublishAt;

  if (publishAt > 0 && publishAt >= actionAt) {
    return { timestamp: publishAt, actor: publishInfo?.publisher || '' };
  }
  if (actionAt > 0) {
    return { timestamp: actionAt, actor: getLastActionEditor(video) || '' };
  }
  return null;
}

export function getClipUpdatedAt(video: OtherVideo): Date {
  const activity = getClipActivity(video);
  if (activity) return new Date(activity.timestamp);
  return new Date(video._updatedAt || video._publishedAt || 0);
}

/** The editor or publisher behind `getClipUpdatedAt`'s winning timestamp. */
export function getClipUpdatedBy(video: OtherVideo): string {
  return getClipActivity(video)?.actor ?? '';
}

export function formatUpdatedDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
}

export function buildContentItemsFromStories(
  homeStories: DashboardStory[],
): ContentItem[] {
  const allContent: ContentItem[] = [];

  homeStories.forEach((story: DashboardStory) => {
    const speakers = story.speakers ?? [];
    // Same source the Story Manager rows use for their summary line.
    const storyDescription =
      getAiDescriptionResponse(story)?.response?.toString().trim() ?? '';

    // Skip removed clips: "removing" a clip soft-deletes it via isHidden, so
    // hidden clips must not surface in Recent Content (matches every other
    // clip listing in the app).
    story.otherVideos
      ?.filter((video: OtherVideo) => !video.isHidden)
      .forEach((video: OtherVideo) => {
        if (!video.id) return;
        const { duration, formattedDuration } =
          getFormattedDurationForStoryVideo(video);
        allContent.push({
          id: video.id,
          storyId: story.id,
          storyTitle: story.title || 'Untitled Story',
          title: video.title || 'Untitled Clip',
          description:
            toDescriptionSnippet(video.transcriptionText) || storyDescription,
          thumbnail:
            video.videoFilePrimary?.video?.thumbnailUrl ||
            video.thumbnail?.url ||
            video.associatedVideos?.find(v => v.videoFilePrimary)
              ?.videoFilePrimary?.video?.thumbnailUrl ||
            story.originalVideo?.video?.thumbnailUrl ||
            story.thumbnail?.url,
          duration: duration ? formattedDuration : undefined,
          contentType: 'clip',
          updatedAt: getClipUpdatedAt(video),
          updatedBy: getClipUpdatedBy(video),
          speakers,
          isPublished: getAllPublishInfo(video).length > 0,
          reviewStatus: video.reviewStatus || DEFAULT_CLIP_REVIEW_STATUS,
          tab: ContentTab.video_clip,
        });
      });

    story.shareableImages?.forEach((image: ShareableImageType) => {
      const parsedMetadata =
        typeof image.metadata === 'string'
          ? (() => {
              try {
                return JSON.parse(image.metadata);
              } catch {
                return null;
              }
            })()
          : image.metadata;
      allContent.push({
        id: image.id,
        storyId: story.id,
        storyTitle: story.title || 'Untitled Story',
        title: image.quote || 'Shareable Image',
        description: storyDescription,
        thumbnail: image.imagefile?.url,
        contentType: 'quote',
        updatedAt: new Date(image._updatedAt || 0),
        updatedBy: parsedMetadata?.editedBy || '',
        speakers,
        isPublished: false,
        tab: ContentTab.pull_quotes,
      });
    });

    if (story.savedBlog) {
      Object.entries(story.savedBlog).forEach(([timestamp, blog]) => {
        if (blog && typeof blog === 'object' && 'title' in blog) {
          allContent.push({
            id: `${story.id}-blog-${timestamp}`,
            storyId: story.id,
            storyTitle: story.title || 'Untitled Story',
            title: blog.title || 'Article',
            description: toDescriptionSnippet(blog.content) || storyDescription,
            thumbnail: undefined,
            contentType: 'article',
            updatedAt: new Date(parseInt(timestamp)),
            updatedBy: blog.username || '',
            speakers,
            isPublished: false,
            tab: ContentTab.blog,
            contentKey: timestamp,
          });
        }
      });
    }

    if (story.savedSocialPost) {
      Object.entries(story.savedSocialPost).forEach(([timestamp, post]) => {
        if (post && typeof post === 'object' && 'title' in post) {
          allContent.push({
            id: `${story.id}-social-${timestamp}`,
            storyId: story.id,
            storyTitle: story.title || 'Untitled Story',
            title: post.title || 'Social Post',
            description: toDescriptionSnippet(post.content) || storyDescription,
            thumbnail: undefined,
            contentType: 'social',
            updatedAt: new Date(parseInt(timestamp)),
            updatedBy: post.username || '',
            speakers,
            isPublished: false,
            tab: ContentTab.social_posts,
            contentKey: timestamp,
          });
        }
      });
    }
  });

  return allContent.sort(
    (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
  );
}

/**
 * Applies a deletion to the cached stories — the inverse of
 * `buildContentItemsFromStories`, narrowed to removal.
 *
 * The list cannot just refetch after a delete. Writes go to the CMA
 * (`site-api.datocms.com`, via the arbor-v1 proxy) while the home list reads
 * the Content Delivery API (`graphql.datocms.com`) — a separate replica that
 * lags the write, so an album query fired straight after a delete can still
 * return the deleted content and put the row back. Editing the cache is what
 * makes the row leave on click; the next natural load re-syncs.
 */
export function removeContentItemsFromStories(
  stories: DashboardStory[],
  deleted: ContentItem[],
): DashboardStory[] {
  if (deleted.length === 0) return stories;

  const deletedByStory = new Map<string, Set<string>>();
  for (const item of deleted) {
    const ids = deletedByStory.get(item.storyId) ?? new Set<string>();
    ids.add(item.id);
    deletedByStory.set(item.storyId, ids);
  }

  return stories.map(story => {
    const deletedIds = deletedByStory.get(story.id);
    if (!deletedIds) return story;

    const withoutDeletedKeys = (
      saved: Record<string, unknown> | undefined,
      prefix: 'blog' | 'social',
    ) => {
      if (!saved) return saved;
      return Object.fromEntries(
        Object.entries(saved).filter(
          ([timestamp]) =>
            !deletedIds.has(`${story.id}-${prefix}-${timestamp}`),
        ),
      );
    };

    return {
      ...story,
      otherVideos: story.otherVideos?.filter(
        v => !v.id || !deletedIds.has(v.id),
      ),
      shareableImages: story.shareableImages?.filter(
        s => !deletedIds.has(s.id),
      ),
      savedBlog: withoutDeletedKeys(story.savedBlog, 'blog'),
      savedSocialPost: withoutDeletedKeys(story.savedSocialPost, 'social'),
    } as DashboardStory;
  });
}

export function getUniqueStories(
  allContentItems: ContentItem[],
): Array<[string, string]> {
  const storyMap = new Map<string, string>();
  allContentItems.forEach(item => {
    if (!storyMap.has(item.storyId)) {
      storyMap.set(item.storyId, item.storyTitle);
    }
  });
  return Array.from(storyMap.entries()).sort((a, b) =>
    a[1].localeCompare(b[1]),
  );
}

/**
 * Comparable form of an editor/publisher name. Content stores a display name
 * typed by whichever client wrote it, so casing and inner spacing vary between
 * the same person's records.
 */
export function normalizeEditorName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * The match set for {@link excludeInternalEditors}, from Arbor staff's person
 * record names.
 *
 * Single-word names are dropped: a `PersonRecord`'s name is auto-derived from
 * the email local part, so Arbor's own records include bare first names
 * ("John", "Joe"). Matching those would hide a customer's content whenever
 * their display name happens to be the same first name — and a hidden row is
 * invisible, where an unhidden Arbor row is merely untidy. Email-shaped names
 * are kept: they are single-word but can't collide with a person's name.
 */
export function toInternalEditorNameSet(
  names: (string | null | undefined)[],
): Set<string> {
  const set = new Set<string>();
  names.forEach(name => {
    const normalized = normalizeEditorName(name ?? '');
    if (!normalized) return;
    if (!normalized.includes(' ') && !normalized.includes('@')) return;
    set.add(normalized);
  });
  return set;
}

/**
 * Drops items whose latest activity was Arbor's own staff, so a customer's Home
 * page shows their team's work and not ours (support edits, publishes made on
 * their behalf). Items with no recorded editor are kept.
 */
export function excludeInternalEditors(
  allContentItems: ContentItem[],
  internalEditorNames: ReadonlySet<string>,
): ContentItem[] {
  if (internalEditorNames.size === 0) return allContentItems;
  return allContentItems.filter(
    item => !internalEditorNames.has(normalizeEditorName(item.updatedBy)),
  );
}

export function getUniqueUpdatedBy(allContentItems: ContentItem[]): string[] {
  const names = new Set<string>();
  allContentItems.forEach(item => {
    if (item.updatedBy) names.add(item.updatedBy);
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

/**
 * Which slice of the org's content the Recent Content table is showing.
 * `all` is a superset of `mine`, not its complement — see the toggle's comment
 * in RecentContentFilters for why "Team" (not-mine) would hide content.
 */
export type ContentOwnerFilter = 'mine' | 'all';

/**
 * Clip status filter values: the publish state plus the existing Content
 * Studio review workflow statuses (same labels as the clip filter dropdown).
 */
export type ClipStatusFilter = 'published' | ClipReviewStatus;

export const CLIP_STATUS_FILTER_OPTIONS: Array<{
  value: ClipStatusFilter;
  label: string;
}> = [
  { value: 'published', label: 'Published' },
  ...CLIP_REVIEW_STATUSES.map(status => ({ value: status, label: status })),
];

/**
 * Review statuses are part of the flag-gated clip review workflow
 * (PLAT-10480); orgs without it only get the publish-state option.
 */
export function getClipStatusFilterOptions(
  showReviewWorkflow: boolean,
): typeof CLIP_STATUS_FILTER_OPTIONS {
  return showReviewWorkflow
    ? CLIP_STATUS_FILTER_OPTIONS
    : CLIP_STATUS_FILTER_OPTIONS.filter(option => option.value === 'published');
}

export function matchesClipStatus(
  item: ContentItem,
  filter: ClipStatusFilter,
): boolean {
  // Only clips carry a status, so any status filter narrows the list to clips.
  if (item.contentType !== 'clip') return false;
  if (filter === 'published') return item.isPublished;
  return item.reviewStatus === filter;
}

/**
 * Unique speakers by display name. Speakers are separate Dato records per
 * story, so the same person appears once per story they're in — keying by
 * name collapses those duplicates for the filter list (which also filters by
 * name, matching every story that person appears in).
 */
export function getUniqueSpeakers(
  allContentItems: ContentItem[],
): DatoSpeaker[] {
  const speakerMap = new Map<string, DatoSpeaker>();
  allContentItems.forEach(item => {
    item.speakers.forEach(speaker => {
      const name = speaker.name?.trim();
      if (name && !speakerMap.has(name)) {
        speakerMap.set(name, { ...speaker, name });
      }
    });
  });
  return Array.from(speakerMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}
